import type { TFunction } from 'i18next';

import type { ExportColumnDefinition } from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import type {
    HealthOverviewParameter,
    HealthParameterKey,
    HealthTrendPoint,
    HealthWoundTrendPoint,
} from '@/domain/health';
import { formatMachineNumber } from '@/utils/localeFormatters';

import { toHealthPercent } from '../_utils/healthDetailOverviewHelpers';
import { HEALTH_OVERVIEW_NAVIGATION_PARAMETER_ORDER } from '../_utils/healthOverviewNavigationOrder';

type HealthOverviewDownloadRow = Record<string, string | number | null>;

export type HealthOverviewWoundColumnKey = (typeof WOUND_DOWNLOAD_COLUMNS)[number];

const WOUND_DOWNLOAD_COLUMNS = [
    'count',
    'temperature',
    'activeWounds',
    'healingWounds',
    'smallWound',
    'mediumWound',
    'bigWound',
    'singleWound',
    'moreThanOneWound',
    'downgradeValue',
] as const;

const LAKSVEL_SCORE_SUFFIXES = ['score0', 'score1', 'score2', 'score3'] as const;

type LaksvelScoreSuffix = (typeof LAKSVEL_SCORE_SUFFIXES)[number];

const WOUND_INDICATOR_LABEL_KEYS: Record<
    Exclude<
        HealthOverviewWoundColumnKey,
        'count' | 'temperature' | 'activeWounds' | 'healingWounds' | 'downgradeValue'
    >,
    string
> = {
    smallWound: 'fishHealthIndicators.smallWound',
    mediumWound: 'fishHealthIndicators.mediumWound',
    bigWound: 'fishHealthIndicators.bigWound',
    singleWound: 'fishHealthIndicators.oneWound',
    moreThanOneWound: 'fishHealthIndicators.moreThanOneWound',
};

const LAKSVEL_SCORE_LABEL_KEYS: Record<LaksvelScoreSuffix, string> = {
    score0: 'dashboard.laksvelScore0',
    score1: 'dashboard.laksvelScore1',
    score2: 'dashboard.laksvelScore2',
    score3: 'dashboard.laksvelScore3',
};

const NON_PERCENTAGE_COLUMNS = new Set(['date', 'count', 'temperature']);

export interface HealthOverviewExportLabelContext {
    locationName: string;
    cageName: string;
    date: string;
    fishCount: string;
    temperature: string;
    woundColumn: (columnKey: HealthOverviewWoundColumnKey) => string;
    parameterScoreColumn: (
        parameterKey: HealthParameterKey,
        scoreSuffix: LaksvelScoreSuffix
    ) => string;
}

function sortParametersByNavigationOrder(
    parameters: HealthOverviewParameter[]
): HealthOverviewParameter[] {
    const rankByKey = new Map(
        HEALTH_OVERVIEW_NAVIGATION_PARAMETER_ORDER.map((key, index) => [key, index])
    );

    return [...parameters].sort((a, b) => {
        const rankA = rankByKey.get(a.key) ?? Number.MAX_SAFE_INTEGER;
        const rankB = rankByKey.get(b.key) ?? Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
    });
}

function getLaksvelDownloadColumnKeys(parameters: HealthOverviewParameter[]): string[] {
    const sortedParameters = sortParametersByNavigationOrder(parameters);

    return sortedParameters.flatMap((parameter) =>
        LAKSVEL_SCORE_SUFFIXES.map((suffix) => `${parameter.key}_${suffix}`)
    );
}

function getExportDataColumnKeys(parameters: HealthOverviewParameter[]): string[] {
    return ['date', ...WOUND_DOWNLOAD_COLUMNS, ...getLaksvelDownloadColumnKeys(parameters)];
}

function isPercentageColumn(columnKey: string): boolean {
    return !NON_PERCENTAGE_COLUMNS.has(columnKey);
}

function toExportColumn(
    columnKey: string,
    labels: HealthOverviewExportLabelContext
): ExportColumnDefinition {
    if (columnKey === 'date') {
        return { name: 'day.date', header: labels.date };
    }
    if (columnKey === 'count') {
        return { name: 'day.count', header: labels.fishCount };
    }
    if (columnKey === 'temperature') {
        return { name: 'day.temperature', header: labels.temperature };
    }

    if (WOUND_DOWNLOAD_COLUMNS.includes(columnKey as HealthOverviewWoundColumnKey)) {
        return {
            name: `day.${columnKey}`,
            header: labels.woundColumn(columnKey as HealthOverviewWoundColumnKey),
        };
    }

    const scoreMatch = columnKey.match(/^(.+)_(score[0-3])$/);
    if (scoreMatch) {
        const [, parameterKey, scoreSuffix] = scoreMatch;
        return {
            name: `day.${columnKey}`,
            header: labels.parameterScoreColumn(
                parameterKey as HealthParameterKey,
                scoreSuffix as LaksvelScoreSuffix
            ),
        };
    }

    return isPercentageColumn(columnKey)
        ? { name: `day.${columnKey}`, header: `${columnKey} (%)` }
        : `day.${columnKey}`;
}

export function createHealthOverviewExportLabelContext(
    tHealth: TFunction<'healthModule'>,
    tGlobal: TFunction,
    getParameterLabel: (key: string) => string
): HealthOverviewExportLabelContext {
    const percentageColumn = (label: string): string =>
        tHealth('dashboard.export.percentageColumn', { label });

    return {
        locationName: tHealth('dashboard.export.locationName'),
        cageName: tHealth('dashboard.export.cageName'),
        date: tHealth('dashboard.export.date'),
        fishCount: tHealth('dashboard.export.fishCount'),
        temperature: tHealth('dashboard.export.temperature'),
        woundColumn: (columnKey) => {
            if (columnKey === 'count') {
                return tHealth('dashboard.export.fishCount');
            }
            if (columnKey === 'temperature') {
                return tHealth('dashboard.export.temperature');
            }
            if (columnKey === 'activeWounds') {
                return percentageColumn(tHealth('dashboard.export.activeWounds'));
            }
            if (columnKey === 'healingWounds') {
                return percentageColumn(tHealth('dashboard.export.healingWounds'));
            }
            if (columnKey === 'downgradeValue') {
                return percentageColumn(tHealth('dashboard.export.downgradeValue'));
            }

            const indicatorLabel = tGlobal(WOUND_INDICATOR_LABEL_KEYS[columnKey]);
            return percentageColumn(indicatorLabel);
        },
        parameterScoreColumn: (parameterKey, scoreSuffix) =>
            tHealth('dashboard.export.parameterScoreColumn', {
                parameter: getParameterLabel(parameterKey),
                score: tHealth(LAKSVEL_SCORE_LABEL_KEYS[scoreSuffix]),
            }),
    };
}

export function buildHealthOverviewExportColumns(
    parameters: HealthOverviewParameter[],
    labels: HealthOverviewExportLabelContext
): ExportColumnDefinition[] {
    const dataColumnKeys = getExportDataColumnKeys(parameters);

    return [
        { name: 'location.name', header: labels.locationName },
        { name: 'cage.name', header: labels.cageName },
        ...dataColumnKeys.map((columnKey) => toExportColumn(columnKey, labels)),
    ];
}

function formatFraction(value: number | null | undefined): string | null {
    return formatMachineNumber(value, 2) ?? null;
}

function formatPercent(value: number | null | undefined): string | null {
    return formatMachineNumber(toHealthPercent(value), 2) ?? null;
}

function mapWoundFields(wound: HealthWoundTrendPoint): HealthOverviewDownloadRow {
    return {
        count: wound.woundCount,
        temperature: formatFraction(wound.temperature),
        activeWounds: formatPercent(wound.woundTypes.numWounds),
        healingWounds: formatPercent(wound.woundTypes.numHealingWounds),
        smallWound: formatPercent(wound.sizeDistribution.smallWound),
        mediumWound: formatPercent(wound.sizeDistribution.mediumWound),
        bigWound: formatPercent(wound.sizeDistribution.bigWound),
        singleWound: formatPercent(wound.distributionOfWounds.oneWound),
        moreThanOneWound: formatPercent(wound.distributionOfWounds.moreThanOneWound),
        downgradeValue: formatPercent(wound.notSuperior),
    };
}

function mapParameterTrendFields(
    parameterKey: string,
    trendPoint: HealthTrendPoint
): HealthOverviewDownloadRow {
    return {
        [`${parameterKey}_score0`]: formatPercent(trendPoint.score0),
        [`${parameterKey}_score1`]: formatPercent(trendPoint.score1),
        [`${parameterKey}_score2`]: formatPercent(trendPoint.score2),
        [`${parameterKey}_score3`]: formatPercent(trendPoint.score3),
    };
}

export function buildHealthOverviewDownloadRows(
    parameters: HealthOverviewParameter[],
    woundDailyTrend: HealthWoundTrendPoint[]
): HealthOverviewDownloadRow[] {
    const dataColumnKeys = getExportDataColumnKeys(parameters);
    const days = new Set<string>();

    for (const parameter of parameters) {
        for (const point of parameter.trend) {
            days.add(point.day);
        }
    }
    for (const point of woundDailyTrend) {
        days.add(point.day);
    }

    const sortedDays = [...days].sort();
    const woundByDay = new Map(woundDailyTrend.map((point) => [point.day, point]));

    return sortedDays.map((day) => {
        const values: HealthOverviewDownloadRow = {
            date: day,
        };

        const wound = woundByDay.get(day);
        if (wound) {
            Object.assign(values, mapWoundFields(wound));
        }

        for (const parameter of parameters) {
            const trendPoint = parameter.trend.find((point) => point.day === day);
            if (trendPoint) {
                Object.assign(values, mapParameterTrendFields(parameter.key, trendPoint));
            }
        }

        const row: HealthOverviewDownloadRow = {};
        for (const key of dataColumnKeys) {
            row[key] = values[key] ?? null;
        }

        return row;
    });
}

export function buildHealthOverviewDownloadData(
    parameters: HealthOverviewParameter[],
    woundDailyTrend: HealthWoundTrendPoint[],
    labels: HealthOverviewExportLabelContext
): { data: HealthOverviewDownloadRow[]; columns: ExportColumnDefinition[] } {
    return {
        data: buildHealthOverviewDownloadRows(parameters, woundDailyTrend),
        columns: buildHealthOverviewExportColumns(parameters, labels),
    };
}
